import React from "react";
import { useQuery } from "@tanstack/react-query";
import adminApi from "../../api/admin";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  ShieldCheck,
  AlertTriangle,
  Globe,
  Users,
  Monitor,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const AdminAuditStatistics = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit-stats"],
    queryFn: async () => {
      const res = await adminApi.getAuditStatistics();
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center text-red-500 py-10">
        Failed to load statistics.
      </div>
    );
  }

  const deviceData = Object.entries(data.device_breakdown).map(
    ([key, value]) => ({
      device: key,
      count: value,
    })
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <Typography variant="h4" className="font-bold text-gray-900">
          Audit Statistics
        </Typography>
        <Typography className="text-gray-600 mt-1">
          Overview of authentication and device activity
        </Typography>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <StatCard
          title="Total Logins"
          value={data.total_logins}
          icon={<ShieldCheck className="w-5 h-5 text-blue-600" />}
        />
        <StatCard
          title="Failed Logins"
          value={data.failed_logins}
          icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
        />
        <StatCard
          title="Unique IPs"
          value={data.unique_ips}
          icon={<Globe className="w-5 h-5 text-blue-600" />}
        />
        <StatCard
          title="Unique Users"
          value={data.unique_users}
          icon={<Users className="w-5 h-5 text-blue-600" />}
        />
      </div>

      {/* Chart + Environment Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device Breakdown Chart */}
        <Card className="lg:col-span-2 shadow-sm border border-gray-200 rounded-3xl">
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <Monitor className="w-5 h-5 text-blue-600" />
              <Typography className="font-semibold text-gray-800">
                Device Breakdown
              </Typography>
            </div>

            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deviceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="device" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Environment Info */}
        <Card className="shadow-sm border border-gray-200 rounded-3xl">
          <CardContent>
            <Typography className="font-semibold text-gray-800 mb-6">
              Most Common Environment
            </Typography>

            <div className="space-y-6">
              <div>
                <Typography className="text-sm text-gray-500">
                  Browser
                </Typography>
                <Typography className="text-lg font-semibold text-gray-900">
                  {data.most_common_browser || "N/A"}
                </Typography>
              </div>

              <div>
                <Typography className="text-sm text-gray-500">
                  Operating System
                </Typography>
                <Typography className="text-lg font-semibold text-gray-900">
                  {data.most_common_os || "N/A"}
                </Typography>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon }) => {
  return (
    <Card className="shadow-sm border border-gray-200 rounded-2xl hover:shadow-md transition-all">
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="p-2 bg-blue-50 rounded-lg">{icon}</div>
        </div>
        <Typography className="text-sm text-gray-500">
          {title}
        </Typography>
        <Typography className="text-2xl font-bold text-gray-900 mt-1">
          {value.toLocaleString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AdminAuditStatistics;